import React from 'react';
import { styled, IconButton, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import * as ImageStore from '../store/Image';
import * as DialogFactory from 'services/Dialog.Factory';
import { DialogProviderContext } from './DialogProvider';
import { Progress } from 'models';
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined';

const StyledFileUpload = styled('input')({
    display: 'none'
});

const StyledErrorList = styled('ul')({
    listStyle: 'none',
    padding: 0,
    '& > li': {
        padding: 0,
        color: 'error'
    }
});

const ImageUploader = (props: { folderId: string }) => {
    const uploadField = React.createRef<HTMLInputElement>();
    const form = React.createRef<HTMLFormElement>();
    const dispatch = useAppDispatch();
    const allImages = useSelector(ImageStore.selectors.images);
    const images = props.folderId ? ImageStore.utils.getImagesForFolder(allImages, props.folderId) : [];
    const imageNames = images.map(i => i.fileName.toLowerCase());

    const validate = (files: FileList): string[] => {
        return [];
    }

    const getProgressBuilder = (totalImages: number) => (complete: boolean, errors?: string[]) => {
        if (errors && errors.length > 0) {
            // error
            return {
                isComplete: true,
                status: <StyledErrorList>{errors.map(error => <li key={error}>{error}</li>)}</StyledErrorList>
            } as Progress;
        }

        if (complete) {
            // success
            return {
                isComplete: true,
                status: <div>{totalImages} {totalImages === 1 ? 'Image' : 'Images'} uploaded successfully</div>
            } as Progress;
        }

        // in progress
        return {
            isComplete: false,
            status: <div>Uploading {totalImages === 1 ? 'image' : 'images'}...</div>
        } as Progress;
    }

    const handleStartUpload = () => {
        // clear the form
        if (form.current) {
            form.current.reset();
        }
        // prompt the user to provide the file to be uploaded
        if (uploadField.current) {
            uploadField.current.click();
        }
    }

    return (
        <DialogProviderContext.Consumer>
            {(dialogContext) => {

                const confirmUpload = (files: FileList) => {
                    const dialog = DialogFactory.buildConfirmUploadDialog(dialogContext, files, props.folderId);
                    dialog.open();
                };

                const confirmDuplicates = (files: FileList): Promise<void> => {
                    return new Promise((resolve) => {
                        // check if there are already images in this folder containing one of the file names to be uploaded.
                        // the user will still be allowed to continue, but it might help them keep cleaner, smaller, data.
                        const uploadFileNames = [] as string[];
                        for (let i = 0; i < files.length; i++) {
                            const name = files.item(i)?.name;
                            if (name) {
                                uploadFileNames.push(name.toLowerCase());
                            }
                        }
                        const duplicateImageNames = uploadFileNames.filter(fn => imageNames.includes(fn));
                        if (duplicateImageNames.length > 0) {
                            const duplicateDialog = DialogFactory.buildConfirmDuplicateImageDialog(dialogContext, duplicateImageNames, resolve);
                            duplicateDialog.open();
                        } else {
                            resolve();
                        }
                    });
                };

                const handleUpload = () => {
                    if (!uploadField.current) {
                        return;
                    }

                    const files = uploadField.current?.files;

                    if (!files || files.length < 1) {
                        console.warn('User did not select any files to upload');
                        return;
                    }

                    confirmDuplicates(files).then(() => confirmUpload(files));

                    // const validationErrors = validate(files);
                    // if (validationErrors && validationErrors.length > 0) {
                    //     console.warn('Image upload validation failed');
                    //     return;
                    // }

                    // const doUpload = () => {
                    //     const totalImages = files.length;
                    //     const getProgress = getProgressBuilder(totalImages);
                    //     const [dialog, updateDialog] = DialogFactory.buildImageUploadDialog(dialogContext, getProgress(false));
                    //     dialog.open();

                    //     dispatch(ImageStore.actionCreators.upload(props.folderId, files, () => {
                    //         updateDialog(getProgress(true));
                    //     }, (error: string) => {
                    //         updateDialog(getProgress(true, [error]));
                    //     }));
                    // };
                }


                return (
                    <form ref={form}>
                        <StyledFileUpload ref={uploadField} multiple type="file" accept="image/png, image/jpeg" onChange={handleUpload} />
                        <Tooltip title="Upload image">
                            <IconButton aria-label="upload image" onClick={handleStartUpload}>
                                <CloudUploadIcon />
                            </IconButton>
                        </Tooltip>
                    </form>
                );
            }}
        </DialogProviderContext.Consumer>
    )
}

export default ImageUploader;